import { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Flame, Loader2, MapPin, Phone } from "lucide-react";
import { HotlineBtn } from "./Kit";
import { COMPANY } from "../../content/site";
import { http, formatApiError } from "../../lib/api";

const HotlineLine = ({ testid }) => (
  <p className="mt-3 flex items-center gap-2 text-sm text-mist">
    <Phone className="h-3.5 w-3.5 text-gold" aria-hidden="true" />
    <a href={COMPANY.hotlineTel} className="text-paper link-underline" data-testid={testid}>Emergency? Call <span className="font-mono text-gold">{COMPANY.hotline}</span></a>
  </p>
);

const GasLine = ({ gas, testid }) => (
  <p className="flex gap-2.5 text-[14px] text-paper" data-testid={testid}>
    <Flame className="mt-0.5 h-4 w-4 shrink-0 text-alert-soft" aria-hidden="true" />
    <span>Smell gas? Leave the property and call the {gas.service} on <a href={gas.tel} className="font-mono font-semibold text-gold" data-testid={`${testid}-number`}>{gas.number}</a>.</span>
  </p>
);

const Nearest = ({ d, p }) => (
  <div className="space-y-5 rounded-2xl border border-gold/25 bg-deep/80 p-5 sm:p-6" data-testid={`${p}-result-nearest`}>
    <p className="flex items-start gap-2.5 text-lg font-semibold text-paper" data-testid={`${p}-result-town`}>
      <MapPin className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" /> Our nationwide network covers {d.town}
    </p>
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
      <HotlineBtn testid={`${p}-result-hotline`} />
      <Link to={`/areas/${d.city.slug}`} data-testid={`${p}-result-city-link`} className="inline-flex items-center gap-1.5 rounded-full border border-gold/30 px-5 py-3 text-[15px] text-paper transition-colors hover:border-gold hover:text-gold">
        See {d.city.name} page <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
    <GasLine gas={d.gas} testid={`${p}-result-gas`} />
    <Link to={`/contact?type=new-client&city=${encodeURIComponent(d.town)}`} state={{ from: `/areas?postcode=${d.outcode}` }} data-testid={`${p}-result-account`} className="inline-flex items-center gap-1.5 text-[15px] text-gold link-underline">
      Open a business account <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </Link>
  </div>
);

const Message = ({ d, p, full }) => (
  <div className="space-y-4 rounded-2xl border border-gold/25 bg-deep/80 p-5 sm:p-6" data-testid={`${p}-result-${d.type}`}>
    <p className="text-lg text-paper" data-testid={`${p}-result-message`}>{d.message}</p>
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <HotlineBtn testid={`${p}-result-hotline`} />
      {full
        ? <a href="#cities" className="text-[15px] text-gold link-underline" data-testid={`${p}-result-list-link`}>See all the cities we cover</a>
        : <Link to="/areas" className="text-[15px] text-gold link-underline" data-testid={`${p}-result-list-link`}>See all the cities we cover</Link>}
    </div>
  </div>
);

export const PostcodeFinder = ({ variant = "full", onResult }) => {
  const full = variant === "full";
  const p = full ? "postcode-finder" : "postcode-finder-compact";
  const id = useId();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [state, setState] = useState({ status: "idle" });

  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim()) return setState({ status: "error", message: "Enter a postcode, for example CV1 2AB." });
    setState({ status: "loading" });
    try {
      const { data } = await http.get("/postcode/lookup", { params: { q } });
      onResult?.(data);
      if (data.type === "exact") return navigate(`/areas/${data.city.slug}`);
      setState({ status: "done", data });
    } catch (err) {
      onResult?.(null);
      setState({ status: "error", message: formatApiError(err.response?.data?.detail) });
    }
  };

  const loading = state.status === "loading";
  return (
    <div data-testid={p} className={full ? "rounded-3xl border hairline bg-deep/60 p-6 sm:p-8" : ""}>
      <form onSubmit={submit} noValidate aria-busy={loading}>
        <label htmlFor={id} className={full ? "block font-display text-2xl font-semibold tracking-tight text-paper" : "block font-mono text-[11px] uppercase tracking-[0.2em] text-gold"}>
          Check your area
        </label>
        <div className={`flex gap-2 ${full ? "mt-5 flex-col sm:flex-row" : "mt-3"}`}>
          <input
            id={id} value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. CV1 2AB" maxLength={10}
            inputMode="text" autoCapitalize="characters" autoComplete="postal-code" spellCheck={false}
            aria-invalid={state.status === "error"} aria-describedby={`${id}-live`} data-testid={`${p}-input`}
            className={`min-w-0 flex-1 rounded-full border border-gold/20 bg-navy/80 px-5 font-mono uppercase tracking-[0.08em] text-paper placeholder:normal-case placeholder:tracking-normal placeholder:text-mist/70 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/30 ${full ? "py-4 text-lg" : "py-3 text-[15px]"}`}
          />
          <button type="submit" disabled={loading} data-testid={`${p}-submit`}
            className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-gold font-semibold text-navy transition-colors hover:bg-[#ffd873] disabled:opacity-70 ${full ? "px-7 py-4" : "px-5 py-3 text-[15px]"}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <ArrowRight className="h-4 w-4" aria-hidden="true" />}
            {loading ? "Checking…" : "Check"}
          </button>
        </div>
        <HotlineLine testid={`${p}-hotline`} />
      </form>
      <div id={`${id}-live`} aria-live="polite" className={state.status === "idle" || loading ? "" : "mt-5"}>
        {state.status === "error" && <p role="alert" className="rounded-xl border border-alert/50 bg-alert/10 px-4 py-3 text-sm text-paper" data-testid={`${p}-error`}>{state.message}</p>}
        {state.status === "done" && (state.data.type === "nearest" ? <Nearest d={state.data} p={p} /> : <Message d={state.data} p={p} full={full} />)}
      </div>
    </div>
  );
};
